import React, { type ReactNode } from "react";
import {
  Flex,
  Text,
  type Icon,
  type TextProps,
  type SpaceProps,
  type LayoutProps,
  type FlexboxProps,
} from "@chakra-ui/react";
import { type Theme } from "contexts/color-theme";
import { DirectionalButton, type DirectionalButtonProps } from "ui/button";
import { IconChip, type IconChipColor } from "ui/chips";

export type TextColumnProps = {
  /**
   * The color theme to use for text colors.
   *
   * @default "light"
   */
  theme?: Theme;

  /**
   * An optional icon chip to display at the top of the column.
   */
  iconChip?: {
    /**
     * The color variant of the icon chip.
     */
    variant: IconChipColor;

    /**
     * The icon in the icon chip.
     */
    icon: typeof Icon;
  };

  /**
   * The title of the column.
   */
  title: ReactNode;

  /**
   * The HTML element to render the title as.
   *
   * @default "h3"
   */
  titleAs?: TextProps["as"];

  /**
   * The description of the column.
   */
  description?: ReactNode;

  /**
   * An optional call to action. Displayed as a directional button.
   */
  cta?: {
    /**
     * Optional label for the call to action button.
     *
     * @default "Learn more"
     */
    label?: string;

    /**
     * The href that the call to action button will link to.
     */
    href: string;

    /**
     * Optional target for the call to action button.
     */
    target?: DirectionalButtonProps["target"];

    /**
     * The variant of the call to action button.
     *
     * @default "secondary"
     */
    variant?: DirectionalButtonProps["variant"];
  };
} & SpaceProps &
  LayoutProps &
  FlexboxProps;

export function TextColumn({
  theme = "light",
  iconChip,
  title,
  titleAs = "h3",
  description,
  cta,
  ...boxProps
}: TextColumnProps) {
  return (
    <Flex align="start" color={`brand.text.${theme}`} direction="column" gap="small" {...boxProps}>
      {iconChip && <IconChip {...iconChip} />}
      <Text as={titleAs} textStyle="headline3" fontSize={{ base: "brand.md", md: "brand.lg" }}>
        {title}
      </Text>
      {description && (
        <Text textStyle="caption1" color={`brand.textSecondary.${theme}`}>
          {description}
        </Text>
      )}
      {cta && (
        <DirectionalButton href={cta.href} target={cta.target} variant={cta.variant ?? "secondary"} mt="auto" px={0}>
          {cta.label ?? "Learn more"}
        </DirectionalButton>
      )}
    </Flex>
  );
}
